import { isValid12HourTimeFormat } from "@/lib/validation/hour";

export const loadTimeSelectOptions = <T>({
    options: _options,
    filterValue,
    baseDate,
    createOption,
    searchOption
  }: {
    options: T[];
    filterValue: string;
    baseDate: Date;
    createOption: (time: Date, filterValue: string) => T;
    searchOption: (option: T, filterValue: string) => boolean;
  }) => {
    if (!filterValue || filterValue.length === 0) return _options;
  
    const options = [..._options].filter((option) =>
      searchOption(option, filterValue)
    ); // copy the options to avoid mutating the original array
  
    if (options.length > 0) return options;
    // has valid filter but is not presented in options
  
    const filters = createTimeFilters(filterValue);
  
    if (filters.length > 0) {
      filters.forEach((filter) => {
        if (isValid12HourTimeFormat(filter)) {
          const filterDurationOption = createOption(baseDate, filter);
          if (filterDurationOption) {
            options.push(filterDurationOption);
          }
        }
      });
    }
  
    return options;
  };
  
  type TimeFilters = string[];
  
  const SUFFIXES = ['am', 'pm'] as const;
  
  enum SuffixLetter {
    A = 'a',
    P = 'p'
  }
  
  function hasFullSuffix(value: string): boolean {
    return SUFFIXES.some(sfx => value.toLowerCase().endsWith(sfx));
  }
  
  function extractLetterSuffix(value: string): SuffixLetter | null {
    const lastChar = value.toLowerCase().slice(-1);
    return lastChar === SuffixLetter.A
      ? SuffixLetter.A
      : lastChar === SuffixLetter.P
      ? SuffixLetter.P
      : null;
  }
  
  /**
   * Parse a 3- or 4-digit numeric string into hours and minutes.
   * e.g. '915' -> { hours: 9, minutes: 15 }
   *      '1130' -> { hours: 11, minutes: 30 }
   */
  function parseNumericTime(value: string): { hours: number; minutes: number } | null {
    if (!/^[0-9]{3,4}$/.test(value)) return null;
  
    const len = value.length;
    const hours = len === 3 ? +value[0] : +value.slice(0, 2);
    const minutes = len === 3 ? +value.slice(1) : +value.slice(2);
  
    return { hours, minutes };
  }
  
  /**
   * Validate that hours and minutes fall within a 12-hour clock range.
   */
  function isValidTime(hours: number, minutes: number): boolean {
    return hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59;
  }
  
  /**
   * Build a filter string by appending suffix and ensuring proper format.
   * If "value" has no colon and is 1-2 digits, treat as hour and pad minutes to "00".
   */
  function buildFilter(value: string, suffix: string): string {
    const trimmed = value.trim();
  
    // If numeric without colon and 1-2 digits, assume whole hour.
    if (/^[0-9]{1,2}$/.test(trimmed)) {
      return `${trimmed}00${suffix}`;
    }
  
    // If numeric with 3-4 digits (handled elsewhere too), just append suffix;
    // colon is preserved if present.
    return `${trimmed}${suffix}`;
  }
  
  export function createTimeFilters(input: string): TimeFilters {
    const value = input.trim();
  
    if (hasFullSuffix(value)) {
      return [value];
    }
  
    // 2. Ends with single-letter suffix 'a' or 'p'
    const letter = extractLetterSuffix(value);
    if (letter) {
      const suffix = letter === SuffixLetter.A ? 'am' : 'pm';
      const base = value.slice(0, -1);
      return [buildFilter(base, suffix)];
    }
  
    // 3. Pure numeric 3-4 digits: generate both 'am' and 'pm'
    const numeric = parseNumericTime(value);
    if (numeric && isValidTime(numeric.hours, numeric.minutes)) {
      const timeStr = `${numeric.hours}:${numeric.minutes.toString().padStart(2, '0')}`;
      return SUFFIXES.map(suff => `${timeStr}${suff}`);
    }
  
    // 4. Fallback: generate both for length-based formatting
    return SUFFIXES.map(suffix => buildFilter(value, suffix));
  }
  